import React from "react";
import { PhaseData } from "../types";
import { formatCurrency, parseInputValue } from "../utils/formatters";
import { calculatePhaseValues } from "../utils/calculations";
import "./JourneyTable.css";

interface JourneyTableProps {
  phaseData: PhaseData[];
  defaultAssetPercents: number[];
  goalTotalAssets: number;
  onPhaseDataChange: (
    index: number,
    field: keyof PhaseData,
    value: number
  ) => void;
  onAssetPercentChange: (index: number, value: number) => void;
}

const JourneyTable: React.FC<JourneyTableProps> = ({
  phaseData,
  defaultAssetPercents,
  goalTotalAssets,
  onPhaseDataChange,
  onAssetPercentChange,
}) => {
  const phases = ["ON-RAMP", "FREEWAY", "NEAR-GOAL", "GOAL"];

  const metrics = [
    {
      label: "Phase Target (% of Goal)",
      key: "phaseTarget" as const,
      editable: true,
      format: "percent" as const,
      tooltip: "Marks the % of your total asset goal at which this phase ends",
    },
    {
      label: "Asset Range (% of Goal)",
      key: "assetRange" as const,
      editable: false,
      format: "text" as const,
      tooltip: "Shows the range of total assets covered during this phase",
    },
    {
      label: "Total Assets",
      key: "totalAssets" as const,
      editable: false,
      format: "currency" as const,
      tooltip: "Total asset value at this phase",
    },
    {
      label: "DTA (%)",
      key: "dta" as const,
      editable: true,
      format: "percent" as const,
      tooltip: "Debt-to-Assets ratio",
    },
    {
      label: "Debt",
      key: "debt" as const,
      editable: false,
      format: "currency" as const,
      tooltip: "Total debt amount",
    },
    {
      label: "DSCR",
      key: "dscr" as const,
      editable: true,
      format: "number" as const,
      tooltip: "Debt Service Coverage Ratio",
    },
    {
      label: "MTY** (Yield) (%)",
      key: "mty" as const,
      editable: false,
      format: "percent" as const,
      tooltip: "Minimum Target Yield",
    },
    {
      label: "MTI* (%)",
      key: "mti" as const,
      editable: true,
      format: "percent" as const,
      tooltip: "Maximum Target Interest",
    },
    {
      label: "Safety Buffer (months)",
      key: "resMonths" as const,
      editable: true,
      format: "number" as const,
      tooltip: "Months of debt service coverage",
    },
    {
      label: "Safety Buffer Value",
      key: "reserveValue" as const,
      editable: false,
      format: "currency" as const,
      tooltip: "Dollar value of safety buffer",
    },
  ];

  const handleCellBlur = (
    phaseIndex: number,
    metricKey: string,
    value: string
  ) => {
    const numValue = parseInputValue(value);

    if (metricKey === "phaseTarget") {
      onAssetPercentChange(phaseIndex, numValue / 100);
    } else {
      const field = metricKey as keyof PhaseData;
      const convertedValue = ["dta", "mti", "mty"].includes(metricKey)
        ? numValue / 100
        : numValue;
      onPhaseDataChange(phaseIndex, field, convertedValue);
    }
  };

  const getCellValue = (
    metric: (typeof metrics)[0],
    phaseIndex: number,
    percent: number
  ): string => {
    const { assets, debt, mty, reserveDollar } = calculatePhaseValues(
      goalTotalAssets,
      percent,
      phaseData[phaseIndex]
    );

    switch (metric.key) {
      case "phaseTarget":
        return phaseIndex === defaultAssetPercents.length - 1
          ? "100%"
          : `${(percent * 100).toFixed(0)}%`;
      case "assetRange":
        const rangeStart =
          phaseIndex === 0 ? 0 : defaultAssetPercents[phaseIndex - 1] * 100;
        const rangeEnd =
          phaseIndex === defaultAssetPercents.length - 1
            ? 100
            : defaultAssetPercents[phaseIndex] * 100;
        return `${rangeStart.toFixed(0)}% to ${rangeEnd.toFixed(0)}%`;
      case "totalAssets":
        return formatCurrency(assets);
      case "dta":
        return `${(phaseData[phaseIndex].dta * 100).toFixed(2)}%`;
      case "debt":
        return formatCurrency(debt);
      case "dscr":
        return phaseData[phaseIndex].dscr.toFixed(2);
      case "mty":
        return `${(mty * 100).toFixed(2)}%`;
      case "mti":
        return `${(phaseData[phaseIndex].mti * 100).toFixed(2)}%`;
      case "resMonths":
        return phaseData[phaseIndex].resMonths.toString();
      case "reserveValue":
        // Recalculate safety buffer value using current phase data
        const currentDebt = assets * phaseData[phaseIndex].dta;
        const currentReserveValue =
          ((currentDebt * phaseData[phaseIndex].mti) / 12) *
          phaseData[phaseIndex].resMonths;
        return formatCurrency(currentReserveValue);
      default:
        return "";
    }
  };

  const getCellClass = (format: string): string => {
    switch (format) {
      case "currency":
        return "currency";
      case "percent":
        return "percentage";
      case "number":
        return "number";
      default:
        return "";
    }
  };

  return (
    <div className="section">
      <h2>Journey Phases & Metrics</h2>
      <p className="section-description">
        This section outlines each phase of your journey, showing how your total
        assets, debt, and reserves evolve as you progress toward your financial
        goal.
      </p>
      <div className="horizontal-table">
        <table>
          <thead>
            <tr>
              <th
                className="metric-label"
                style={{
                  background: "#f1f5f9",
                  color: "#475569",
                  fontWeight: "600",
                  fontSize: "0.875rem",
                  borderBottom: "2px solid #e2e8f0",
                }}
              >
                Metric
              </th>
              {phases.map((phase) => (
                <th
                  key={phase}
                  className="phase-header"
                  style={{
                    background: "#f1f5f9",
                    color: "#475569",
                    fontWeight: "600",
                    fontSize: "0.875rem",
                    textTransform: "none",
                    letterSpacing: "0",
                    borderBottom: "2px solid #e2e8f0",
                  }}
                >
                  {phase}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metrics.map((metric) => (
              <tr key={metric.key}>
                <td className="metric-label tooltip" title={metric.tooltip}>
                  {metric.label}
                </td>
                {defaultAssetPercents.map((percent, phaseIndex) => {
                  const cellValue = getCellValue(metric, phaseIndex, percent);
                  // Phase Target row: Only Goal column (last column) should NOT be editable
                  const isEditable =
                    metric.editable &&
                    !(
                      metric.key === "phaseTarget" &&
                      phaseIndex === defaultAssetPercents.length - 1
                    );

                  return (
                    <td
                      key={phaseIndex}
                      className={`data-cell ${getCellClass(metric.format)} ${
                        isEditable ? "editable" : ""
                      }`}
                      contentEditable={isEditable}
                      suppressContentEditableWarning={true}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.currentTarget.blur();
                        }
                      }}
                      onBlur={(e) => {
                        if (isEditable) {
                          handleCellBlur(
                            phaseIndex,
                            metric.key,
                            e.currentTarget.textContent || ""
                          );
                        }
                      }}
                    >
                      {cellValue}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="footnotes">
        <p>* MTI = Maximum Target Interest</p>
        <p>** MTY = Minimum Target Yield</p>
      </div>
    </div>
  );
};

export default JourneyTable;
